import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, CalendarCheck, ShieldCheck, Clock, MapPin, Star, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { BUSINESS } from "@/data/siteData";

const ease = [0.22, 1, 0.36, 1];

const HERO_SLIDES = [
  { src: "/hero-carousel/luxury-car.jpg", alt: "Mobile VIN verification on a high-end passenger car", label: "Cars of every make and model" },
  { src: "/hero-carousel/pickup-truck.jpg", alt: "VIN verifier inspecting a pickup truck with the hood open", label: "Pickup trucks" },
  { src: "/hero-carousel/dealership-fleet.jpg", alt: "VIN verification for a dealership fleet with multiple cars", label: "Dealerships and fleets" },
  { src: "/hero-carousel/commercial-van.jpg", alt: "VIN verifier checking the federal label inside a commercial van", label: "Commercial vans" },
  { src: "/hero-carousel/motorcycle.jpg", alt: "Mobile VIN verification for a motorcycle", label: "Motorcycles" },
  { src: "/hero-carousel/boat-trailer.jpg", alt: "VIN verifier inspecting the identification area at the front of a boat trailer", label: "Trailers" },
  { src: "/hero-carousel/rv-motorhome.jpg", alt: "VIN verifier inspecting the rear identification area of a motorhome", label: "RVs and motorhomes" },
  { src: "/hero-carousel/commercial-box-truck.jpg", alt: "Mobile VIN verification for a commercial box truck", label: "Commercial trucks" },
];

export default function Hero() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (isPaused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % HERO_SLIDES.length),
      5000
    );
    return () => window.clearInterval(timer);
  }, [isPaused]);

  const showPrevious = () => setActiveSlide((current) => (current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const showNext = () => setActiveSlide((current) => (current + 1) % HERO_SLIDES.length);

  return (
    <section id="home" data-testid="hero-section" className="relative pt-[68px] bg-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-mist to-white" aria-hidden />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Copy */}
        <div>
          <motion.div
            initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
            className="inline-flex items-center gap-2 rounded-full bg-brand/10 text-brand px-3.5 py-1.5 text-xs font-semibold"
          >
            <ShieldCheck size={14} /> California Licensed · Verified & Bonded
          </motion.div>

          <motion.h1
            initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05, ease }}
            className="font-display font-extrabold text-ink tracking-tight mt-5 text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05]"
          >
            Mobile VIN Verification <span className="text-brand">in Orange County.</span>
          </motion.h1>

          <motion.p
            initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12, ease }}
            className="mt-5 text-slate2 text-base sm:text-lg leading-relaxed max-w-xl"
          >
            We come to your home, workplace or dealership in Orange County and Los Angeles. VIN verification for sedans, SUVs, pickup trucks,
            classic cars and out-of-state vehicles. We also verify eligible motorcycles, trailers, motorhomes,
            RVs and commercial vehicles.
          </motion.p>

          <motion.div
            initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16, ease }}
            className="mt-5 flex flex-wrap items-center gap-2"
          >
            <span className="flex items-center gap-1.5 text-slate2 text-sm font-medium mr-1">
              <MapPin size={15} className="text-accent" /> Also serving:
            </span>
            {["Orange County", "Los Angeles", "Inland Empire (Riverside & nearby)"].map((a) => (
              <span key={a} className="inline-flex items-center rounded-full bg-brand/[0.08] border border-brand/15 text-brand text-xs font-semibold px-3 py-1.5">
                {a}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.19, ease }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a data-testid="hero-call-btn" href={BUSINESS.phoneHref} className="btn-accent rounded-full px-7 py-4 text-base font-semibold flex items-center justify-center gap-2">
              <Phone size={18} /> Call (657) 768-6189
            </a>
            <button data-testid="hero-book-btn" onClick={() => go("#book")} className="btn-outline rounded-full px-7 py-4 text-base font-semibold flex items-center justify-center gap-2">
              <CalendarCheck size={18} /> Book Appointment
            </button>
          </motion.div>

          <motion.div
            initial={false} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate2"
          >
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-brand" /> Licensed & Bonded</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-brand" /> Same-Day Availability</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-brand" /> We Come To You</span>
          </motion.div>
        </div>

        {/* Image */}
        <motion.div
          initial={false} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1, ease }}
          className="relative"
        >
          <div
            className="group relative h-[340px] sm:h-[440px] lg:h-[500px] rounded-[1.6rem] overflow-hidden shadow-[0_30px_60px_rgba(10,37,145,0.16)] border border-white bg-slate-100"
            role="region"
            aria-roledescription="carousel"
            aria-label="Vehicles we verify"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setIsPaused(false);
            }}
          >
            {HERO_SLIDES.map((slide, index) => (
              <img
                key={slide.src}
                src={slide.src}
                alt={index === activeSlide ? slide.alt : ""}
                aria-hidden={index !== activeSlide}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${index === activeSlide ? "opacity-100" : "opacity-0"}`}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : "auto"}
              />
            ))}

            <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/65 to-transparent pointer-events-none" aria-hidden />
            <div className="absolute bottom-5 right-5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
              {HERO_SLIDES[activeSlide].label}
            </div>

            <button type="button" onClick={showPrevious} aria-label="Previous vehicle" className="absolute left-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink shadow-lg transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-accent">
              <ChevronLeft size={20} />
            </button>
            <button type="button" onClick={showNext} aria-label="Next vehicle" className="absolute right-3 top-1/2 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-ink shadow-lg transition hover:bg-white focus:outline-none focus:ring-2 focus:ring-accent">
              <ChevronRight size={20} />
            </button>

            <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-1.5" aria-label={`Slide ${activeSlide + 1} of ${HERO_SLIDES.length}`}>
              {HERO_SLIDES.map((slide, index) => (
                <button key={slide.src} type="button" onClick={() => setActiveSlide(index)} aria-label={`Show ${slide.label}`} aria-current={index === activeSlide ? "true" : undefined} className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${index === activeSlide ? "w-6 bg-white" : "w-2 bg-white/60 hover:bg-white"}`} />
              ))}
            </div>

            <button type="button" onClick={() => setIsPaused((current) => !current)} aria-label={isPaused ? "Resume slideshow" : "Pause slideshow"} className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/65 focus:outline-none focus:ring-2 focus:ring-white">
              {isPaused ? <Play size={15} fill="currentColor" /> : <Pause size={15} fill="currentColor" />}
            </button>
          </div>
          {/* Floating rating badge */}
          <div className="absolute -bottom-5 left-5 sm:left-8 card-soft !rounded-2xl px-4 py-3 flex items-center gap-3">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-accent text-accent" />)}
            </div>
            <div className="leading-tight">
              <div className="text-ink font-bold text-sm">5.0 / 5.0</div>
              <div className="text-slate2 text-xs">5-star Google rating</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
